import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, ExpressCheckoutElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { format } from "date-fns";
import { CalendarIcon, MapPin, Clock, Users, Briefcase, ArrowLeft, ArrowRight, X, Loader2, Navigation, CreditCard, CheckCircle } from "lucide-react";
import { cn } from "@/lib/utils";

const STEPS = ["Location", "Details", "Payment"];

// Texas Star
const TexasStar = ({ className = "", size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
  </svg>
);

// Stripe promise singleton
let stripePromise = null;
const getStripePromise = (publishableKey) => {
  if (!stripePromise && publishableKey) {
    stripePromise = loadStripe(publishableKey);
  }
  return stripePromise;
};

// Google Maps script loader singleton
let googleMapsLoaded = false;
let googleMapsLoadPromise = null;

const loadGoogleMaps = (apiKey) => {
  if (googleMapsLoaded && window.google?.maps?.places) {
    return Promise.resolve();
  }
  
  if (googleMapsLoadPromise) {
    return googleMapsLoadPromise;
  }
  
  googleMapsLoadPromise = new Promise((resolve, reject) => {
    // Check if already loaded
    if (window.google?.maps?.places) {
      googleMapsLoaded = true;
      resolve();
      return;
    }
    
    // Check if script tag already exists
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => {
        googleMapsLoaded = true;
        resolve();
      });
      return;
    }
    
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.id = "google-maps-script";
    
    script.onload = () => {
      googleMapsLoaded = true;
      resolve();
    };
    
    script.onerror = (error) => {
      googleMapsLoadPromise = null;
      reject(error);
    };
    
    document.head.appendChild(script);
  });
  
  return googleMapsLoadPromise;
};

// Google Places Autocomplete with proper cleanup
const PlacesAutocomplete = ({ value, onChange, placeholder, testId }) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const listenerRef = useRef(null);
  const [inputValue, setInputValue] = useState(value || "");
  const mountedRef = useRef(true);

  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  useEffect(() => {
    mountedRef.current = true;
    
    const initAutocomplete = () => {
      // Safety checks
      if (!mountedRef.current) return;
      if (!inputRef.current) return;
      if (autocompleteRef.current) return;
      if (!window.google?.maps?.places) return;
      
      try {
        // Create autocomplete instance
        const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
          componentRestrictions: { country: "us" },
          fields: ["formatted_address", "geometry", "name"],
          types: ["geocode", "establishment"]
        });
        
        autocompleteRef.current = autocomplete;
        
        // Set Texas bounds
        const texasBounds = new window.google.maps.LatLngBounds(
          new window.google.maps.LatLng(25.8371, -106.6466),
          new window.google.maps.LatLng(36.5007, -93.5083)
        );
        autocomplete.setBounds(texasBounds);
        
        // Add listener with safety check
        listenerRef.current = autocomplete.addListener("place_changed", () => {
          if (!mountedRef.current) return;
          try {
            const place = autocomplete.getPlace();
            const addr = place?.formatted_address || place?.name;
            if (addr && mountedRef.current) {
              setInputValue(addr);
              onChange(addr);
            }
          } catch (e) {
            // Ignore errors when component is unmounting
          }
        });
        
      } catch (error) {
        console.error("Error initializing autocomplete:", error);
      }
    };
    
    // Try to initialize if Google Maps is already loaded
    if (window.google?.maps?.places) {
      // Small delay to ensure DOM is ready
      const timeoutId = setTimeout(initAutocomplete, 100);
      return () => {
        clearTimeout(timeoutId);
        mountedRef.current = false;
        
        // Cleanup listener
        if (listenerRef.current && window.google?.maps?.event) {
          try {
            window.google.maps.event.removeListener(listenerRef.current);
          } catch (e) {
            // Ignore cleanup errors
          }
          listenerRef.current = null;
        }
        
        // Unbind autocomplete from input to prevent focus errors
        if (autocompleteRef.current) {
          try {
            window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
          } catch (e) {
            // Ignore cleanup errors
          }
          autocompleteRef.current = null;
        }
      };
    }
    
    return () => {
      mountedRef.current = false;
    };
  }, [onChange]);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleBlur = () => {
    if (inputValue && inputValue !== value) {
      onChange(inputValue);
    }
  };

  return (
    <input
      ref={inputRef}
      type="text"
      value={inputValue}
      onChange={handleInputChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      autoComplete="off"
      className="w-full h-12 px-4 bg-white/5 border border-white/10 rounded-md text-white placeholder:text-white/40 focus:outline-none focus:border-[#C5A572]/50"
      data-testid={testId}
    />
  );
};

// Integrated Payment Form Component with Express Checkout (Apple Pay / Google Pay)
const PaymentForm = ({ booking, clientSecret, onSuccess, apiUrl }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [expressCheckoutReady, setExpressCheckoutReady] = useState(false);
  const [showCardForm, setShowCardForm] = useState(false);
  const [availableWallets, setAvailableWallets] = useState({});

  // Handle Express Checkout (Apple Pay / Google Pay) confirm
  const onExpressCheckoutConfirm = async () => {
    if (!stripe || !elements) return;
    
    setLoading(true);
    setPaymentError(null);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}/success/${booking.id}`,
        },
        redirect: "if_required"
      });

      if (error) {
        setPaymentError(error.message);
        toast.error(error.message);
      } else if (paymentIntent && paymentIntent.status === "succeeded") {
        try {
          await axios.post(`${apiUrl}/payments/confirm/${booking.id}`);
        } catch (e) {
          console.error("Failed to confirm payment on backend:", e);
        }
        toast.success("Payment successful!");
        onSuccess();
      }
    } catch (err) {
      setPaymentError("Payment failed. Please try again.");
      toast.error("Payment failed. Please try again.");
    }
    
    setLoading(false);
  };

  // Handle Card Payment submit
  const handleCardSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) return;

    setLoading(true);
    setPaymentError(null);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/success/${booking.id}`,
        },
        redirect: "if_required"
      });

      if (error) {
        setPaymentError(error.message);
        toast.error(error.message);
      } else if (paymentIntent && paymentIntent.status === "succeeded") {
        try {
          await axios.post(`${apiUrl}/payments/confirm/${booking.id}`);
        } catch (e) {
          console.error("Failed to confirm payment on backend:", e);
        }
        toast.success("Payment successful!");
        onSuccess();
      } else if (paymentIntent && paymentIntent.status === "requires_action") {
        const { error: confirmError } = await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: `${window.location.origin}/success/${booking.id}`,
          },
        });
        if (confirmError) {
          setPaymentError(confirmError.message);
          toast.error(confirmError.message);
        }
      }
    } catch (err) {
      setPaymentError("An unexpected error occurred.");
      toast.error("Payment failed. Please try again.");
    }

    setLoading(false);
  };

  return (
    <div className="space-y-5">
      {/* Express Checkout - Apple Pay / Google Pay Buttons */}
      <div data-testid="express-checkout-buttons" className="space-y-3">
        <ExpressCheckoutElement
          options={{
            buttonType: {
              applePay: 'buy',
              googlePay: 'buy'
            },
            buttonTheme: {
              applePay: 'black',
              googlePay: 'black'
            },
            buttonHeight: 55,
            layout: {
              maxColumns: 1,
              maxRows: 3
            },
            paymentMethodOrder: ['applePay', 'googlePay']
          }}
          onReady={({ availablePaymentMethods }) => {
            setAvailableWallets(availablePaymentMethods || {});
            if (availablePaymentMethods) {
              setExpressCheckoutReady(true);
            }
          }}
          onConfirm={onExpressCheckoutConfirm}
          onClick={({ resolve }) => {
            resolve();
          }}
        />
      </div>

      {/* Divider */}
      <div className="flex items-center gap-4 py-1">
        <div className="flex-1 h-px bg-white/10" />
        <span className="text-white/30 text-xs uppercase tracking-widest">or pay with card</span>
        <div className="flex-1 h-px bg-white/10" />
      </div>

      {/* Card Payment Form */}
      {!showCardForm ? (
        <button
          onClick={() => setShowCardForm(true)}
          className="w-full py-4 bg-white/5 border border-white/20 rounded-lg text-white hover:bg-white/10 transition-colors flex items-center justify-center gap-3 font-medium"
          data-testid="show-card-form-btn"
        >
          <CreditCard className="w-5 h-5" />
          Pay with Credit Card
        </button>
      ) : (
        <form onSubmit={handleCardSubmit} className="space-y-4">
          <div className="bg-[#0A0A0A] rounded-lg p-4 border border-white/10">
            <PaymentElement 
              options={{
                layout: "tabs",
                wallets: {
                  applePay: "never",
                  googlePay: "never",
                  link: "never"
                }
              }}
            />
          </div>
          
          {paymentError && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
              <p className="text-red-400 text-sm">{paymentError}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={!stripe || loading}
            className="btn-gold w-full flex items-center justify-center gap-3 py-4"
            data-testid="submit-payment-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <CreditCard className="w-5 h-5" />
                Pay ${booking.total_price}
              </>
            )}
          </button>
        </form>
      )}

      {/* Loading overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[#0A0A0A] rounded-lg p-8 flex flex-col items-center gap-4">
            <Loader2 className="w-10 h-10 text-[#C5A572] animate-spin" />
            <p className="text-white">Processing payment...</p>
          </div>
        </div>
      )}

      <p className="text-center text-white/40 text-xs">
        Secure payment powered by Stripe
      </p>
    </div>
  );
};

// Main Booking Wizard
const BookingWizard = ({ apiUrl, onComplete, onCancel }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [priceLoading, setPriceLoading] = useState(false);
  const [googleLoaded, setGoogleLoaded] = useState(false);
  const [bookingCreated, setBookingCreated] = useState(null);
  const [stripeKey, setStripeKey] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentReady, setPaymentReady] = useState(false);
  
  // Service type: "transfer" or "hourly"
  const [serviceType, setServiceType] = useState("transfer");
  
  // Common fields
  const [pickupAddress, setPickupAddress] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");
  const [vehicle, setVehicle] = useState(null);
  const [priceInfo, setPriceInfo] = useState(null);
  
  // Transfer-specific fields
  const [dropoffAddress, setDropoffAddress] = useState("");
  
  // Hourly-specific fields
  const [selectedHours, setSelectedHours] = useState(3);
  const [itineraryNotes, setItineraryNotes] = useState("");
  const [hourlyRate, setHourlyRate] = useState(75);
  
  // Customer details
  const [passengers, setPassengers] = useState(1);
  const [luggage, setLuggage] = useState(0);
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [specialRequests, setSpecialRequests] = useState("");

  // Load Google Maps safely
  useEffect(() => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      console.error("Google Maps API key not found");
      return;
    }
    
    loadGoogleMaps(apiKey)
      .then(() => {
        setGoogleLoaded(true);
      })
      .catch((error) => {
        console.error("Failed to load Google Maps:", error);
      });
  }, []);

  useEffect(() => { 
    fetchVehicle(); 
    fetchStripeConfig();
    fetchPricingInfo();
  }, [apiUrl]);

  const fetchStripeConfig = async () => {
    try {
      const res = await axios.get(`${apiUrl}/config`);
      if (res.data?.stripe_publishable_key) {
        setStripeKey(res.data.stripe_publishable_key);
      }
    } catch (e) { 
      console.error("Failed to load Stripe config:", e); 
    }
  };

  const fetchVehicle = async () => {
    try {
      const res = await axios.get(`${apiUrl}/vehicle`);
      setVehicle(res.data);
    } catch (e) { 
      console.error("Failed to fetch vehicle:", e); 
    }
  };

  const fetchPricingInfo = async () => {
    try {
      const res = await axios.get(`${apiUrl}/pricing-info`);
      if (res.data?.hourly_rate) {
        setHourlyRate(res.data.hourly_rate);
      }
    } catch (e) { 
      console.error("Failed to fetch pricing info:", e); 
    }
  };

  const calculatePrice = useCallback(async () => {
    if (serviceType === "transfer") {
      if (!pickupAddress || !dropoffAddress) return;
      setPriceLoading(true);
      try {
        const res = await axios.post(`${apiUrl}/calculate-price`, {
          pickup_address: pickupAddress,
          dropoff_address: dropoffAddress
        });
        setPriceInfo(res.data);
      } catch (e) {
        if (e.response?.data?.detail) toast.error(e.response.data.detail);
        setPriceInfo(null);
      }
      setPriceLoading(false);
    }
  }, [apiUrl, pickupAddress, dropoffAddress, serviceType]);

  // Calculate hourly price
  const calculateHourlyPrice = useCallback(() => {
    const total = Math.ceil(selectedHours * hourlyRate);
    setPriceInfo({
      total_price: total,
      hours: selectedHours,
      hourly_rate: hourlyRate
    });
  }, [selectedHours, hourlyRate]);

  useEffect(() => {
    if (serviceType === "transfer") {
      const t = setTimeout(() => { 
        if (pickupAddress && dropoffAddress) calculatePrice(); 
      }, 500);
      return () => clearTimeout(t);
    }
  }, [pickupAddress, dropoffAddress, calculatePrice, serviceType]);

  // Calculate hourly price when hours change
  useEffect(() => {
    if (serviceType === "hourly") {
      calculateHourlyPrice();
    }
  }, [serviceType, selectedHours, calculateHourlyPrice]);

  // Reset price info when switching service type
  useEffect(() => {
    setPriceInfo(null);
    if (serviceType === "hourly") {
      calculateHourlyPrice();
    }
  }, [serviceType]);

  const validateStep = () => {
    if (currentStep === 0) {
      if (!pickupAddress) { toast.error("Enter pickup address"); return false; }
      if (serviceType === "transfer" && !dropoffAddress) { toast.error("Enter dropoff address"); return false; }
      if (!priceInfo) { toast.error("Wait for price calculation"); return false; }
      if (!selectedDate) { toast.error("Select a date"); return false; }
      if (!selectedTime) { toast.error("Select a time"); return false; }
    }
    if (currentStep === 1) {
      if (!customerName.trim()) { toast.error("Enter your name"); return false; }
      if (!customerEmail.includes("@")) { toast.error("Enter valid email"); return false; }
      if (!customerPhone.trim()) { toast.error("Enter phone number"); return false; }
    }
    return true;
  };

  const handleNext = async () => {
    if (!validateStep()) return;
    if (currentStep === 1) {
      setLoading(true);
      try {
        // Create booking based on service type
        const bookingData = {
          booking_type: serviceType,
          pickup_address: serviceType === "transfer" ? (priceInfo.pickup_formatted || pickupAddress) : pickupAddress,
          dropoff_address: serviceType === "transfer" ? (priceInfo.dropoff_formatted || dropoffAddress) : "",
          pickup_date: format(selectedDate, "yyyy-MM-dd"),
          pickup_time: selectedTime,
          distance_miles: serviceType === "transfer" ? priceInfo.distance_miles : 0,
          duration_minutes: serviceType === "transfer" ? priceInfo.duration_minutes : selectedHours * 60,
          hours: serviceType === "hourly" ? selectedHours : 0,
          itinerary_notes: serviceType === "hourly" ? itineraryNotes : "",
          passengers, 
          luggage,
          customer_name: customerName,
          customer_email: customerEmail,
          customer_phone: customerPhone,
          special_requests: specialRequests,
          total_price: priceInfo.total_price
        };

        const res = await axios.post(`${apiUrl}/bookings`, bookingData);
        
        setBookingCreated(res.data);
        
        // Create PaymentIntent immediately
        const intentRes = await axios.post(`${apiUrl}/payments/create-intent`, {
          booking_id: res.data.id
        });
        
        setClientSecret(intentRes.data.client_secret);
        setPaymentReady(true);
        setCurrentStep(2);
        
      } catch (e) { 
        toast.error("Error creating booking"); 
        console.error(e);
      }
      setLoading(false);
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePaymentSuccess = () => {
    if (bookingCreated) {
      window.location.href = `/success/${bookingCreated.id}`;
    }
  };

  const timeSlots = [];
  for (let h = 0; h < 24; h++) {
    for (let m of [0, 30]) {
      const hh = h.toString().padStart(2, "0");
      const mm = m.toString().padStart(2, "0");
      const p = h < 12 ? "AM" : "PM";
      const dh = h === 0 ? 12 : h > 12 ? h - 12 : h;
      timeSlots.push({ value: `${hh}:${mm}`, label: `${dh}:${mm} ${p}` });
    }
  }

  // Stripe Elements appearance
  const stripeAppearance = {
    theme: 'night',
    variables: {
      colorPrimary: '#C5A572',
      colorBackground: '#0A0A0A',
      colorText: '#ffffff',
      colorDanger: '#ef4444',
      fontFamily: 'system-ui, sans-serif',
      borderRadius: '8px',
    },
    rules: {
      '.Input': {
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
      },
      '.Input:focus': {
        border: '1px solid rgba(212, 175, 55, 0.5)',
      },
      '.Tab': {
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
      },
      '.Tab--selected': {
        backgroundColor: 'rgba(212, 175, 55, 0.2)',
        borderColor: '#C5A572',
      },
    },
  };

  return (
    <div className="glass rounded-lg p-6 md:p-10" data-testid="booking-wizard">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <button 
          onClick={onCancel} 
          className="text-white/50 hover:text-white flex items-center gap-2 text-sm" 
          data-testid="cancel-booking-btn"
        >
          <X className="w-4 h-4" /> Cancel
        </button>
        <div className="flex items-center gap-2">
          <TexasStar size={16} className="text-[#C5A572]" />
          <span className="text-white/50 text-sm">Step {currentStep + 1} of {STEPS.length}</span>
        </div>
      </div>

      {/* Steps */}
      <div className="flex items-center justify-center gap-2 mb-10">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center">
            <div className={cn(
              "w-2.5 h-2.5 rounded-full transition-all", 
              i === currentStep && "bg-[#C5A572] w-8", 
              i < currentStep && "bg-green-500", 
              i > currentStep && "bg-white/20"
            )} />
            {i < STEPS.length - 1 && <div className="w-8 h-px bg-white/10 mx-1" />}
          </div>
        ))}
      </div>

      <div className="min-h-[400px]">
        {/* Step 1: Location */}
        {currentStep === 0 && (
          <div className="space-y-6 animate-fade-in" data-testid="step-location">
            {/* Service Type Tabs */}
            <div className="flex justify-center mb-8">
              <div className="inline-flex p-1 bg-white/5 border border-white/10 rounded-sm">
                <button
                  onClick={() => setServiceType("transfer")}
                  className={cn(
                    "px-8 py-3 text-xs font-semibold uppercase tracking-[0.2em] transition-all",
                    serviceType === "transfer" 
                      ? "bg-[#C5A572] text-black" 
                      : "text-white/60 hover:text-white"
                  )}
                  data-testid="tab-transfer"
                >
                  Transfer
                </button>
                <button
                  onClick={() => setServiceType("hourly")}
                  className={cn(
                    "px-8 py-3 text-xs font-semibold uppercase tracking-[0.2em] transition-all",
                    serviceType === "hourly" 
                      ? "bg-[#C5A572] text-black" 
                      : "text-white/60 hover:text-white"
                  )}
                  data-testid="tab-hourly"
                >
                  Hourly
                </button>
              </div>
            </div>

            <div className="text-center mb-8">
              <h2 className="font-serif text-3xl text-white mb-2">
                {serviceType === "transfer" ? "Where are you headed?" : "Book by the Hour"}
              </h2>
              <p className="text-white/50">
                {serviceType === "transfer" 
                  ? "Point-to-point transfer service" 
                  : `$${hourlyRate}/hour • ${3} hour minimum`}
              </p>
            </div>

            {vehicle && (
              <div className="flex items-center gap-4 p-4 glass-light rounded-sm mb-6">
                <img src={vehicle.images[0]} alt={vehicle.name} className="w-20 h-14 object-cover rounded-sm" />
                <div>
                  <h3 className="font-serif text-lg text-white">{vehicle.name}</h3>
                  <p className="text-white/50 text-xs flex gap-3">
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {vehicle.capacity}</span>
                    <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" /> {vehicle.luggage_capacity} bags</span>
                  </p>
                </div>
              </div>
            )}

            <div className="space-y-5">
              {/* Pickup Address - Always shown */}
              <div className="space-y-2">
                <Label className="text-white/70 flex items-center gap-2 text-sm">
                  <MapPin className="w-4 h-4 text-[#C5A572]" /> Pickup Location
                </Label>
                {googleLoaded ? (
                  <PlacesAutocomplete 
                    value={pickupAddress} 
                    onChange={setPickupAddress} 
                    placeholder="Enter pickup address..." 
                    testId="pickup-input" 
                  />
                ) : (
                  <Input disabled placeholder="Loading Google Maps..." className="h-12 bg-white/5 border-white/10" />
                )}
              </div>

              {/* Dropoff Address - Only for Transfer */}
              {serviceType === "transfer" && (
                <div className="space-y-2">
                  <Label className="text-white/70 flex items-center gap-2 text-sm">
                    <Navigation className="w-4 h-4 text-[#C5A572]" /> Dropoff Location
                  </Label>
                  {googleLoaded ? (
                    <PlacesAutocomplete 
                      value={dropoffAddress} 
                      onChange={setDropoffAddress} 
                      placeholder="Enter dropoff address..." 
                      testId="dropoff-input" 
                    />
                  ) : (
                    <Input disabled placeholder="Loading Google Maps..." className="h-12 bg-white/5 border-white/10" />
                  )}
                </div>
              )}

              {/* Duration Selector - Only for Hourly */}
              {serviceType === "hourly" && (
                <div className="space-y-2">
                  <Label className="text-white/70 flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4 text-[#C5A572]" /> Duration
                  </Label>
                  <Select value={selectedHours.toString()} onValueChange={(v) => setSelectedHours(parseInt(v))}>
                    <SelectTrigger className="h-12 bg-white/5 border-white/10 text-white text-sm" data-testid="duration-select">
                      <SelectValue placeholder="Select duration" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      <SelectItem value="3" className="text-white hover:bg-white/10">3 Hours (Minimum)</SelectItem>
                      <SelectItem value="4" className="text-white hover:bg-white/10">4 Hours</SelectItem>
                      <SelectItem value="5" className="text-white hover:bg-white/10">5 Hours</SelectItem>
                      <SelectItem value="6" className="text-white hover:bg-white/10">6 Hours</SelectItem>
                      <SelectItem value="8" className="text-white hover:bg-white/10">8 Hours</SelectItem>
                      <SelectItem value="10" className="text-white hover:bg-white/10">10 Hours</SelectItem>
                      <SelectItem value="12" className="text-white hover:bg-white/10">12 Hours (Full Day)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Date and Time */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white/70 flex items-center gap-2 text-sm">
                    <CalendarIcon className="w-4 h-4 text-[#C5A572]" /> Date
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button 
                        className={cn(
                          "w-full h-12 px-4 text-left bg-white/5 border border-white/10 rounded-sm flex items-center justify-between text-sm", 
                          !selectedDate ? "text-white/40" : "text-white"
                        )} 
                        data-testid="date-picker-trigger"
                      >
                        {selectedDate ? format(selectedDate, "MMM d, yyyy") : "Select"}
                        <CalendarIcon className="w-4 h-4 text-white/40" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[#0A0A0A] border-white/10" align="start">
                      <Calendar 
                        mode="single" 
                        selected={selectedDate} 
                        onSelect={setSelectedDate} 
                        disabled={(d) => d < new Date()} 
                        className="bg-[#0A0A0A] text-white" 
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label className="text-white/70 flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4 text-[#C5A572]" /> Time
                  </Label>
                  <Select value={selectedTime} onValueChange={setSelectedTime}>
                    <SelectTrigger className="h-12 bg-white/5 border-white/10 text-white text-sm" data-testid="time-select">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-[300px]">
                      {timeSlots.map(t => (
                        <SelectItem key={t.value} value={t.value} className="text-white hover:bg-white/10">
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Itinerary Notes - Only for Hourly */}
              {serviceType === "hourly" && (
                <div className="space-y-2">
                  <Label className="text-white/70 text-sm">
                    Itinerary Notes (Optional)
                  </Label>
                  <textarea 
                    value={itineraryNotes} 
                    onChange={e => setItineraryNotes(e.target.value)} 
                    placeholder="Describe your planned stops or destinations..." 
                    rows={2} 
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-sm text-white placeholder:text-white/30 resize-none focus:outline-none focus:border-[#C5A572]/50 text-sm" 
                    data-testid="itinerary-notes" 
                  />
                </div>
              )}
            </div>

            {/* Price Display */}
            {(priceLoading || priceInfo) && (
              <div className="glass-light rounded-sm p-5 mt-6" data-testid="price-display">
                {priceLoading ? (
                  <div className="flex items-center justify-center py-2">
                    <Loader2 className="w-5 h-5 text-[#C5A572] animate-spin mr-2" />
                    <span className="text-white/50 text-sm">Calculating...</span>
                  </div>
                ) : priceInfo && (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white/40 text-xs uppercase tracking-widest">
                        {serviceType === "transfer" ? "Estimated Trip" : "Hourly Service"}
                      </p>
                      <p className="text-white text-sm mt-1">
                        {serviceType === "transfer" 
                          ? `${priceInfo.distance_miles} miles • ${priceInfo.duration_text}`
                          : `${priceInfo.hours} hours @ $${priceInfo.hourly_rate}/hr`
                        }
                      </p>
                    </div>
                    <p className="price-display text-4xl font-medium">${priceInfo.total_price}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Details */}
        {currentStep === 1 && (
          <div className="space-y-6 animate-fade-in" data-testid="step-details">
            <div className="text-center mb-8">
              <h2 className="font-serif text-3xl text-white mb-2">Your Details</h2>
              <p className="text-white/50">Tell us who's traveling</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white/70 text-sm">Passengers</Label>
                <Select value={passengers.toString()} onValueChange={v => setPassengers(+v)}>
                  <SelectTrigger className="h-12 bg-white/5 border-white/10 text-white" data-testid="passengers-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10">
                    {[...Array(vehicle?.capacity || 6)].map((_, i) => (
                      <SelectItem key={i+1} value={(i+1).toString()} className="text-white hover:bg-white/10">
                        {i+1}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-white/70 text-sm">Bags</Label>
                <Select value={luggage.toString()} onValueChange={v => setLuggage(+v)}>
                  <SelectTrigger className="h-12 bg-white/5 border-white/10 text-white" data-testid="luggage-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10">
                    {[...Array((vehicle?.luggage_capacity || 6) + 1)].map((_, i) => (
                      <SelectItem key={i} value={i.toString()} className="text-white hover:bg-white/10">
                        {i}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-white/70 text-sm">Full Name *</Label>
                <Input 
                  value={customerName} 
                  onChange={e => setCustomerName(e.target.value)} 
                  placeholder="John Smith" 
                  className="h-12 bg-white/5 border-white/10 text-white placeholder:text-white/30" 
                  data-testid="customer-name-input" 
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/70 text-sm">Email *</Label>
                <Input 
                  type="email" 
                  value={customerEmail} 
                  onChange={e => setCustomerEmail(e.target.value)} 
                  placeholder="john@example.com" 
                  className="h-12 bg-white/5 border-white/10 text-white placeholder:text-white/30" 
                  data-testid="customer-email-input" 
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/70 text-sm">Phone *</Label>
                <Input 
                  type="tel" 
                  value={customerPhone} 
                  onChange={e => setCustomerPhone(e.target.value)} 
                  placeholder="+1 (555) 123-4567" 
                  className="h-12 bg-white/5 border-white/10 text-white placeholder:text-white/30" 
                  data-testid="customer-phone-input" 
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/70 text-sm">Special Requests</Label>
                <textarea 
                  value={specialRequests} 
                  onChange={e => setSpecialRequests(e.target.value)} 
                  placeholder="Child seat, flight number..." 
                  rows={2} 
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-md text-white placeholder:text-white/30 resize-none focus:outline-none focus:border-[#C5A572]/50 text-sm" 
                  data-testid="special-requests-textarea" 
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Payment */}
        {currentStep === 2 && bookingCreated && (
          <div className="space-y-6 animate-fade-in" data-testid="step-payment">
            <div className="text-center mb-6">
              <h2 className="font-serif text-3xl text-white mb-2">Complete Your Booking</h2>
              <p className="text-white/50">Payment is required to confirm your reservation</p>
            </div>

            {/* Booking Summary */}
            <div className="glass-light rounded-sm p-5 space-y-4 mb-6">
              {/* Service Type Badge */}
              <div className="flex items-center justify-between">
                <span className={cn(
                  "px-3 py-1 text-xs font-semibold uppercase tracking-wider",
                  serviceType === "hourly" 
                    ? "bg-[#C5A572]/20 text-[#C5A572] border border-[#C5A572]/30" 
                    : "bg-white/10 text-white/70 border border-white/20"
                )}>
                  {serviceType === "hourly" ? "Hourly Service" : "Transfer Service"}
                </span>
                {serviceType === "hourly" && (
                  <span className="text-[#C5A572] text-sm font-medium">
                    {bookingCreated.hours} Hours
                  </span>
                )}
              </div>

              <div className="flex items-center gap-4">
                <img src={vehicle?.images[0]} alt="GMC Yukon XL" className="w-16 h-12 object-cover rounded-sm" />
                <div>
                  <p className="text-white font-serif">GMC Yukon XL</p>
                  <p className="text-white/50 text-xs">
                    {passengers} passenger{passengers > 1 ? "s" : ""}, {luggage} bag{luggage !== 1 ? "s" : ""}
                  </p>
                </div>
              </div>
              
              <div className="h-px bg-white/10" />
              
              {/* Different layout based on service type */}
              {serviceType === "transfer" ? (
                <>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-white/40 text-xs uppercase">Pickup</p>
                      <p className="text-white truncate">{bookingCreated.pickup_address}</p>
                    </div>
                    <div>
                      <p className="text-white/40 text-xs uppercase">Dropoff</p>
                      <p className="text-white truncate">{bookingCreated.dropoff_address}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-white/40 text-xs uppercase">Date & Time</p>
                      <p className="text-white">{bookingCreated.pickup_date} at {bookingCreated.pickup_time}</p>
                    </div>
                    <div>
                      <p className="text-white/40 text-xs uppercase">Contact</p>
                      <p className="text-white">{bookingCreated.customer_name}</p>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-white/40 text-xs uppercase">Pickup Location</p>
                      <p className="text-white truncate">{bookingCreated.pickup_address}</p>
                    </div>
                    <div>
                      <p className="text-white/40 text-xs uppercase">Duration</p>
                      <p className="text-white">{bookingCreated.hours} Hours @ ${hourlyRate}/hr</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-white/40 text-xs uppercase">Date & Time</p>
                      <p className="text-white">{bookingCreated.pickup_date} at {bookingCreated.pickup_time}</p>
                    </div>
                    <div>
                      <p className="text-white/40 text-xs uppercase">Contact</p>
                      <p className="text-white">{bookingCreated.customer_name}</p>
                    </div>
                  </div>
                  {bookingCreated.itinerary_notes && (
                    <div className="text-sm">
                      <p className="text-white/40 text-xs uppercase">Itinerary Notes</p>
                      <p className="text-white/70">{bookingCreated.itinerary_notes}</p>
                    </div>
                  )}
                </>
              )}
              
              <div className="h-px bg-white/10" />
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/50 text-sm">Total Amount</p>
                  {serviceType === "hourly" && (
                    <p className="text-white/30 text-xs">{bookingCreated.hours} hours × ${hourlyRate}</p>
                  )}
                </div>
                <p className="price-display text-3xl">${bookingCreated.total_price}</p>
              </div>
            </div>

            {/* Integrated Payment Form */}
            {stripeKey && clientSecret && paymentReady ? (
              <Elements 
                stripe={getStripePromise(stripeKey)} 
                options={{
                  clientSecret,
                  appearance: stripeAppearance,
                }}
              >
                <PaymentForm 
                  booking={bookingCreated}
                  clientSecret={clientSecret}
                  onSuccess={handlePaymentSuccess}
                  apiUrl={apiUrl}
                />
              </Elements>
            ) : (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 text-[#C5A572] animate-spin mx-auto mb-4" />
                <p className="text-white/50">Loading payment form...</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Navigation */}
      {currentStep < 2 && (
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/10">
          <button 
            onClick={() => setCurrentStep(p => Math.max(p - 1, 0))} 
            disabled={currentStep === 0} 
            className={cn(
              "btn-secondary flex items-center gap-2", 
              currentStep === 0 && "opacity-50 cursor-not-allowed"
            )} 
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <button 
            onClick={handleNext} 
            disabled={loading} 
            className="btn-primary flex items-center gap-2" 
            data-testid="next-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Creating...
              </>
            ) : (
              <>
                {currentStep === 1 ? "Continue to Payment" : "Continue"} 
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default BookingWizard;
